import { ExerciseCard } from "./ExerciseCard";
import { SelectedExercise } from "../ExerciseSelectionDialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";

interface ExerciseLibraryGridProps {
  exercises: SelectedExercise[];
  loading: boolean;
  multiSelectMode: boolean;
  onSelectExercise: (exercise: SelectedExercise) => void;
  selectedExercises: SelectedExercise[];
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export function ExerciseLibraryGrid({
  exercises,
  loading,
  multiSelectMode,
  onSelectExercise,
  selectedExercises,
  currentPage,
  setCurrentPage
}: ExerciseLibraryGridProps) {
  const itemsPerPage = 24;
  const isMobile = useIsMobile();
  const { t } = useTranslations("workouts");
  
  if (loading) {
    return <div className="text-center py-8 dark:text-white">{t("exerciseLibrary.loading")}</div>;
  }
  
  if (exercises.length === 0) {
    return <div className="text-center py-8 dark:text-white">{t("exerciseLibrary.noExercisesFound")}</div>;
  }
  
  const totalPages = Math.ceil(exercises.length / itemsPerPage);
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExercises = exercises.slice(startIndex, startIndex + itemsPerPage);
  
  const renderPaginationItems = () => {
    const items = [];
    
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
            }}
            isActive={currentPage === i}
            className="dark:text-white dark:hover:text-white/80"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };
  
  return (
    <div className="space-y-6 pb-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 w-full">
        {paginatedExercises.map((exercise) => {
          const selectedIndex = selectedExercises.findIndex(ex => ex.id === exercise.id);
          const isSelected = selectedIndex !== -1;
          
          return (
            <ExerciseCard
              key={exercise.id}
              exercise={exercise}
              index={selectedIndex}
              selectedCount={selectedExercises.length}
              multiSelectMode={multiSelectMode}
              onSelectExercise={onSelectExercise}
              isSelected={isSelected}
            />
          );
        })}
      </div>
      
      {totalPages > 1 && (
        <Pagination className="mt-6">
          <PaginationContent className="flex-wrap justify-center">
            <PaginationItem>
              <PaginationPrevious 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) setCurrentPage(currentPage - 1);
                }}
                className={`dark:text-white dark:hover:text-white/80 ${currentPage === 1 ? "pointer-events-none opacity-50" : ""}`}
                text={t('pagination.previous')}
              />
            </PaginationItem>
            
            {renderPaginationItems()}
            
            <PaginationItem>
              <PaginationNext 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                }}
                className={`dark:text-white dark:hover:text-white/80 ${currentPage === totalPages ? "pointer-events-none opacity-50" : ""}`}
                text={t('pagination.next')}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
